import styled from '@emotion/styled';
import { Input, Select, Tooltip } from 'antd';
import DownSvg from 'assets/svg/Down';
import { colorConstants } from 'config/colorConstants';
import { useContext, useEffect, useMemo, useState, useRef, useCallback } from 'react';
import DatePickerWithEpoch from 'components/atoms/EpochDatePicker';
import tw from 'twin.macro';
import { PromoType, PromoTactic, TacticType, OfferMethod, ProductPPG } from 'types/tradePromoOptimization';
import { TPODataContext } from 'contexts/TPOContext';
import { useCommonApiHandler } from 'hooks/general/useCommonApiHandler';
import { tradePromoOptimizationUrls } from 'config/urls';
import _ from 'lodash';
import { MetricsApiTriggerPoints } from '../Promos';
import RangeInputCustom from 'components/molecules/common/RangeInput';
import OfferDropdown from '../../OfferDropdown';
import { useFetchOfferData } from 'hooks/tradePromoOptimization/useFetchOfferData';
import { getConfig, getEditButtonState, sendNotification } from 'utils/helperMethods';
import { UserContext } from 'contexts/ConfigContext';
import { ConditionRule, FieldCondition, FieldTypes, FormField, OperatorTypes } from 'types/tpo/promoInfo';
import { PromoInfoFormFieldNames, PromotionTypesShortNames, PROMO_LIMITS, TPO_QUERY_KEYS } from 'constants/tpo';
import { DownOutlined, UpOutlined, InfoCircleOutlined } from '@ant-design/icons';
import dayjs from 'dayjs';
import { ConfigKeys, PromotionTypes, tpoPromoMetricKeys } from 'utils/Enums';
import ErrorPopover from '../../ErrorPopOver';
import { queryClient } from 'services/reactQueryService';
import {
    computeCascadingKPIs,
    getPromoMetricByNameKeyName,
    handleNumberInputKeyDown,
    isPromoYearChangeNotAllowed,
} from '../../tpoHelperMethods';
import { hasInvalidChars } from 'utils/inputHelper';
import { IconTextPopover, PopoverItem } from 'components/atoms/IconTextPopover/IconTextPopover';
import utc from 'dayjs/plugin/utc';
import AutoPopulateIcon from 'components/atoms/AutoPopulateIcon/AutoPopulateIcon';
import { tpoLogger } from 'utils/logger/tpoLogger';
import CombinedInput from 'components/molecules/common/CombinedInput';
import { usePromoInfoFormFields } from 'config/usePromoInfoFormFields';
dayjs.extend(utc);

// todo: need to move all the dropdown values to db to make it configurable
const offerMethodDropdownOptions: { label: string; value: OfferMethod }[] = [
    { label: 'Promo library', value: 'promo_library' },
    { label: 'Manual', value: 'manual' },
    // { label: 'Previous', value: 'previous' },
];
const suggestedOfferTypeDropdownOptions: { label: string; value: string }[] = [
    { label: 'Uplift', value: 'tpo_uplift_percent' },
    { label: 'MPIC', value: 'mpic' },
    { label: 'Inc. Net Sales', value: 'incremental_sales' },
];

export const manualOfferDropdownOptions: { label: string; value: string }[] = [
    { label: 'Discount', value: 'price_off' },
    { label: 'Single', value: 'single' },
    { label: 'Buy Get', value: 'combination_offer' },
    { label: 'Buy For', value: 'buy_for' },
    { label: 'Buy Save', value: 'buy_save' },

    // { label: '2_FOR', value: '2_for' },
    // { label: '3_FOR', value: '3_for' },
];

const featureQualityDropdownOptions: { label: string; value: string }[] = [
    { label: 'Feature A', value: 'feature_a' },
    { label: 'Feature B', value: 'feature_b' },
    { label: 'Feature C', value: 'feature_c' },
];

const displayQualityDropdownOptions: { label: string; value: string }[] = [
    { label: 'Display A', value: 'display_a' },
    { label: 'Display B', value: 'display_b' },
    { label: 'Display C', value: 'display_c' },
];

const promoStatusDropdownOptions: { label: string; value: string; disabled: boolean }[] = [
    { label: 'Draft', value: 'draft', disabled: false },
    { label: 'Planned', value: 'planned', disabled: false },
    { label: 'Submitted for Approval', value: 'submitted_for_approval', disabled: true },
    { label: 'Pending Approval', value: 'pending_approval', disabled: true },
    { label: 'Rejected', value: 'rejected', disabled: true },
    { label: 'Approved', value: 'approved', disabled: true },
    { label: 'Customer Accepted', value: 'customer_accepted', disabled: true },
    { label: 'Cancelled', value: 'cancelled', disabled: true },
    { label: 'Executed', value: 'executed', disabled: true },
];

const allowedTransitionsApproved: Record<string, string[]> = {
    draft: ['planned'],
    planned: ['draft', 'submitted_for_approval'],
    submitted_for_approval: [],
    pending_approval: [],
    rejected: ['planned', 'submitted_for_approval'],
    approved: ['customer_accepted'],
    customer_accepted: ['cancelled'],
    executed: [],
    cancelled: [],
};

const allowedTransitionsUnapproved: Record<string, string[]> = {
    draft: ['planned'],
    planned: ['draft'],
    submitted_for_approval: [],
    pending_approval: [],
    rejected: [],
    approved: [],
    customer_accepted: [],
    executed: [],
    cancelled: [],
};

const statusesForApproved = ['draft', 'planned', 'approved', 'rejected', 'customer_accepted', 'customer accepted'];
const statusesForUnapproved = ['draft', 'planned', 'executed'];

const promoFundingReqDropdownOptions: { label: string; value: string; disabled: boolean }[] = [
    { label: 'Meet Comp Funds', value: 'meet_comp_funds', disabled: false },
    { label: 'Incremental Volume', value: 'incremental_volume', disabled: false },
    { label: 'National Overlay', value: 'national_overlay', disabled: false },
];

const spendMetricsForOfferLibrary: string[] = [
    'edlp_spends',
    'off_invoice_rate',
    'nboi_rate',
    'bill_back_rate',
    'off_invoice_percent',
    'scan_volume',
    'fixed_ad_fees',
    'fixed_display_fees',
    'fixed_markdown_fees',
    'fixed_misc',
    'slotting_fixed',
    'fixed_coupon_clearing_house',
    'fixed_non_coupon_clearing_house',
    'fixed_program_cost',
];

const promoMetricsForOfferLibrary: string[] = [
    'list_price_per_unit',
    'percent_of_consumption',
    'cogs_per_unit',
    'tpo_adjusted_base_units',
];

const CustomSelect = styled(Select)`
    ${tw`border !border-borderGreyAl1 !rounded-md text-[13px] text-headerColor`}
    .ant-select-selector {
        height: 100% !important;
        display: flex;
        align-items: center;
        box-shadow: none !important;
        border: none !important;
    }
    .ant-select-arrow {
        ${tw`text-headerColor`}
    }
`;

interface TempOfferDetailsType {
    manualOffer: string | null;
    buyGet: [number, number] | null;
    buyFor: [number, number] | null;
    buySave: [number, number] | null;
    discount: number | null;
    comboUnit: 'abs' | '%' | 'item' | null;
    discountUnit: 'abs' | '%' | null;
    buySaveUnit: 'abs' | '%' | null;
}
interface IProps {
    setFetchMetricsTriggeredFrom: React.Dispatch<React.SetStateAction<MetricsApiTriggerPoints>>;
    fetchMetricsTriggeredFrom: MetricsApiTriggerPoints;
    mechanicType: string;
    eventId: null | string;
    isSavePending: boolean;
}

const PromoInfoSection: React.FC<IProps> = ({
    fetchMetricsTriggeredFrom,
    setFetchMetricsTriggeredFrom,
    mechanicType,
    eventId,
    isSavePending,
}) => {
    const {
        selectedPlanInfo,
        selectedEventInfo,
        tempEventProperties,
        updateSelectedEventProperty,
        promoConfig,
        onGetMetricRecomValuesSuccess,
        isValid,
        setIsValid,
        viewMode,
        isOptimizationInProgress,
        isCurrentPromoFormDisabled,
        isTpmTab,
        promoPopupErrors,
        selectedPromoPPGIds,
        upliftModelMutation,
        promoActiveTab,
        dimensionConfigData,
        promoConfigDictionary,
        hasCurrentPromoTacticInitialized,
        finalCalenderStatues,
    } = useContext(TPODataContext);
    const { appConfig } = useContext(UserContext);
    const { dateFormat } = getConfig(ConfigKeys.COMMON_CONFIG, appConfig);
    const {
        units: { currency },
    } = getConfig(ConfigKeys.COMMON_CONFIG, appConfig);

    const keysForRecommendedMetrics = [
        'promoType',
        'tacticType',
        'promoStartDate',
        'promoEndDate',
        'ppgs',
        'craStartDate',
        'craEndDate',
    ];

    const [triggerOfferApi, setTriggerOfferApi] = useState(false);
    const formFields = usePromoInfoFormFields();
    const [promoInfoFormFields, setPromoInfoFormFields] = useState(formFields);
    const [isExpanded, setIsExpanded] = useState<boolean>(true);
    const [tempOfferDetails, setTempOfferDetails] = useState<TempOfferDetailsType | null>(null);

    // Debounced function for fetchUpliftData
    const debouncedFetchUpliftData = useCallback(
        _.debounce(async (ppgs: ProductPPG[]) => {
            await fetchUpliftData(ppgs);
        }, 500), // 500ms delay
        [],
    );

    const allowDropdownClearProperties = [PromoInfoFormFieldNames.FundingRequest];

    const variablesForRecommendedMetrics = useMemo(() => {
        const variables = Object.fromEntries(
            keysForRecommendedMetrics.map((key) => {
                switch (key) {
                    case 'promoStartDate':
                        return ['start_date', selectedEventInfo?.currentPromoTactic?.[key]];
                    case 'promoEndDate':
                        return ['end_date', selectedEventInfo?.currentPromoTactic?.[key]];
                    // if ppgs change is triggering the api send only the newly added ones
                    // else send the current selected ones
                    case 'ppgs':
                        return [
                            'ppgs',
                            fetchMetricsTriggeredFrom === MetricsApiTriggerPoints.PPG
                                ? selectedEventInfo?.currentPromoTactic?.newAddedPPgs
                                : selectedPromoPPGIds,
                        ];
                    default:
                        return [_.snakeCase(key), selectedEventInfo?.currentPromoTactic?.[key], selectedPromoPPGIds];
                }
            }),
        );

        return {
            ...variables,
            dim2_attribute_name: selectedPlanInfo?.dim2Filter?.value[0],
            plan_id: selectedPlanInfo?.planId,
            event_id: eventId,
            is_tpm_plan: selectedPlanInfo?.isTpmPlan,
        };
    }, [selectedEventInfo?.currentPromoTactic, fetchMetricsTriggeredFrom]);

    useEffect(() => {
        setPromoInfoFormFields((prev) => {
            let isFormValid = true;
            const newFields = prev.map((item) => {
                const fieldProperties: any = getFieldProperties(item.conditions);
                const planStatus = finalCalenderStatues?.approval_status?.toLowerCase();
                const isEditDisable = getEditButtonState(
                    planStatus,
                    selectedEventInfo,
                    tempEventProperties?.CRAStartDate,
                );
                // validation for required fields
                if (isFormValid && item.isRequired && !(fieldProperties?.isHidden || fieldProperties?.isDisabled)) {
                    isFormValid =
                        item.type === FieldTypes.Range
                            ? !!(
                                  selectedEventInfo?.currentPromoTactic?.[item.name]?.[0] &&
                                  selectedEventInfo?.currentPromoTactic?.[item.name]?.[1]
                              )
                            : !!selectedEventInfo?.currentPromoTactic?.[item.name];
                }
                if (fieldProperties)
                    return {
                        ...item,
                        isDisabled: !!fieldProperties.isDisabled || isEditDisable.isDisabled,
                        isHidden: !!fieldProperties.isHidden,
                    };
                return { ...item, isDisabled: item?.isDisabled || isEditDisable.isDisabled };
            });
            setIsValid(isFormValid);

            return newFields;
        });
    }, [
        selectedEventInfo?.currentPromoTactic,
        selectedEventInfo?.is_ever_successfully_published,
        tempEventProperties?.CRAStartDate,
        finalCalenderStatues?.approval_status,
    ]);

    const generatePromoName = (): string => {
        if (tempEventProperties && tempEventProperties.brandAbbreviation) {
            let newName = tempEventProperties.brandAbbreviation.substring(0, PROMO_LIMITS.BRAND_MAX_LENGTH);
            const extraChars = PROMO_LIMITS.BRAND_MAX_LENGTH - newName.length;
            if (selectedEventInfo?.currentPromoTactic?.promoStartDate) {
                const utcConvertedDate = dayjs.unix(selectedEventInfo?.currentPromoTactic?.promoStartDate).utc(); // Ensure UTC interpretation
                newName += `_${utcConvertedDate.format('MM.DD')}`;
            }
            if (mechanicType && selectedEventInfo?.currentPromoTactic?.promoType) {
                let shortMechanicType = mechanicType;
                if (mechanicType.length > PROMO_LIMITS.MECHANIC_MAX_LENGTH) {
                    let mechanicTypeParts = shortMechanicType.split('_');
                    mechanicTypeParts[0] =
                        PromotionTypesShortNames[
                            selectedEventInfo?.currentPromoTactic?.promoType as keyof typeof PromotionTypesShortNames
                        ];
                    shortMechanicType = mechanicTypeParts.join('_');
                }
                newName += `_${shortMechanicType.substring(0, PROMO_LIMITS.MECHANIC_MAX_LENGTH + extraChars)}`;
            }
            return newName;
        }
        return '';
    };

    const handleAutoPopulate = (fieldName: string) => {
        if (fieldName === 'name') {
            const generatedName = generatePromoName();
            if (generatedName) {
                updatePromoDetailsProperty([{ [fieldName]: generatedName }]);
            }
        }
    };

    const prevValuesRef = useRef<Record<string, any>>({});
    const hasHandledInitialLoad = useRef(false);
    const lastPromoIdRef = useRef<string | null>(null);

    const relevantKeys = [
        'promoType',
        'offerType',
        'offer',
        'manualOffer',
        'comboUnit',
        'discount',
        'discountUnit',
        'buyGet',
        'buyFor',
        'buySave',
        'buySaveUnit',
        'suggestedOffer',
    ];

    useEffect(() => {
        const currentTactic = selectedEventInfo?.currentPromoTactic;
        if (!currentTactic || !hasCurrentPromoTacticInitialized || !isValid || viewMode) return;

        const currentPromoId = currentTactic.promoId;

        // If this is the first time loading a new promo, skip the effect once
        const isNewPromoLoad = lastPromoIdRef.current !== currentPromoId;

        if (isNewPromoLoad && !hasHandledInitialLoad.current) {
            tpoLogger.debug('Skipping first load for new promo', { promoId: currentPromoId });
            lastPromoIdRef.current = currentPromoId;
            hasHandledInitialLoad.current = true;

            // Prime the prevValues so future diffs work
            relevantKeys.forEach((key) => {
                prevValuesRef.current[key] = currentTactic[key];
            });

            return;
        }

        const hasValueChanged = (prevValue: any, currentValue: any): boolean => {
            // Handle null/undefined cases
            if (prevValue === currentValue) return false;
            if (prevValue == null || currentValue == null) return true;

            // Handle arrays
            if (Array.isArray(prevValue) && Array.isArray(currentValue)) {
                if (prevValue.length !== currentValue.length) return true;
                return prevValue.some((item, index) => item !== currentValue[index]);
            }

            // Handle objects (for nested objects if needed)
            if (typeof prevValue === 'object' && typeof currentValue === 'object') {
                const prevKeys = Object.keys(prevValue);
                const currentKeys = Object.keys(currentValue);

                if (prevKeys.length !== currentKeys.length) return true;
                return prevKeys.some((key) => hasValueChanged(prevValue[key], currentValue[key]));
            }

            // Handle primitive values
            return prevValue !== currentValue;
        };

        // Check if any relevant property has actually changed
        const hasRelevantChange = relevantKeys.some((key) => {
            const prevValue = prevValuesRef.current[key];
            const currentValue = currentTactic[key];
            const hasChanged = hasValueChanged(prevValue, currentValue);

            if (hasChanged) {
                tpoLogger.debug('Property changed', {
                    key,
                    from: prevValue,
                    to: currentValue,
                    promoId: currentTactic.promoId,
                });
            }

            return hasChanged;
        });

        // Only proceed if relevant properties changed AND all conditions are met
        if (
            hasRelevantChange &&
            [PromotionTypes.Merchandising, PromotionTypes.SM_MDF].includes(Number(currentTactic.promoType))
        ) {
            // Use debounced function instead of direct call
            debouncedFetchUpliftData(currentTactic.ppgs);

            // Update reference values post change
            relevantKeys.forEach((key) => {
                prevValuesRef.current[key] = currentTactic[key];
            });

            // Reset last promoId after fetch so it doesn't skip next tab switch
            lastPromoIdRef.current = currentPromoId;
            hasHandledInitialLoad.current = true;
        }
    }, [
        selectedEventInfo?.currentPromoTactic,
        isValid,
        hasCurrentPromoTacticInitialized,
        viewMode,
        debouncedFetchUpliftData,
    ]);

    // Cleanup debounced function on unmount
    useEffect(() => {
        return () => {
            debouncedFetchUpliftData.cancel();
            // debouncedUpdatePriceOff.cancel();
        };
    }, [debouncedFetchUpliftData]);

    const { loading: metricsRecommValuesLoading } = useCommonApiHandler({
        method: 'post',
        queryKey: [TPO_QUERY_KEYS.GET_METRICS_RECOMMENDED_VALUES, variablesForRecommendedMetrics],
        queryConfig: {
            enabled:
                isValid &&
                !!fetchMetricsTriggeredFrom &&
                variablesForRecommendedMetrics?.ppgs?.length > 0 &&
                selectedEventInfo?.currentPromoTactic?.offerType !== 'promo_library',
        },
        url: `${tradePromoOptimizationUrls.getMetricsRecommendedValues}`,
        variables: variablesForRecommendedMetrics,
        parserFunction: async (res: any) => {
            try {
                // after reset fetchMetricsTriggeredFrom, we don't want to call the api again
                if (fetchMetricsTriggeredFrom === MetricsApiTriggerPoints.None) return res;
                const ppgs = onGetMetricRecomValuesSuccess(res.ppgs, fetchMetricsTriggeredFrom);
                if (
                    [
                        MetricsApiTriggerPoints.PPG,
                        MetricsApiTriggerPoints.Date,
                        MetricsApiTriggerPoints.Tactic,
                    ].includes(fetchMetricsTriggeredFrom) &&
                    [PromotionTypes.Merchandising, PromotionTypes.SM_MDF].includes(
                        Number(selectedEventInfo?.currentPromoTactic?.promoType),
                    )
                ) {
                    await fetchUpliftData(ppgs);
                }
                setFetchMetricsTriggeredFrom(MetricsApiTriggerPoints.None);
                return res;
            } catch (error) {
                throw error;
            }
        },
    });

    const variablesForPromoLibraryPpgMetrics = useMemo(() => {
        const promo = selectedEventInfo?.currentPromoTactic;
        const ppgs = promo?.ppgs.filter((ppg) => ppg.isSelected).map((ppg) => ppg.id);
        return {
            promotion_type: promoConfigDictionary.promotions[Number(promo?.promoType)],
            tactic_description: promoConfigDictionary.tactics[Number(promo?.tacticType)],
            dim2_attribute_name: selectedPlanInfo?.dim2Filter.value[0],
            ppgs,
            mechanic: promo?.suggestedOffer,
        };
    }, [selectedEventInfo?.currentPromoTactic]);

    const { loading: promoLibraryPpgMetricsLoading } = useCommonApiHandler({
        queryKey: ['promo_library_ppg_metrics', variablesForPromoLibraryPpgMetrics],
        method: 'post',
        variables: variablesForPromoLibraryPpgMetrics,
        queryConfig: {
            enabled: isValid && !!selectedEventInfo?.currentPromoTactic?.suggestedOffer,
        },
        url: tradePromoOptimizationUrls.getPromolibraryPpgMetrics,
        parserFunction: (res) => {
            // Process all API response items and collect updates
            let updatedPPGs = [...(selectedEventInfo?.currentPromoTactic?.ppgs || [])];

            res.forEach((item: any) => {
                const { dim1_id } = item;
                const metricKeys = ['promoted_srp', 'tpo_uplift_percent'];
                const spendKeys = ['scan_redemption_percent', 'fixed_ad_fees', 'scan_rate', 'fixed_display_fees'];

                // Find and update the specific PPG
                const ppgIndex = updatedPPGs.findIndex((ppg) => ppg.id === dim1_id);
                if (ppgIndex !== -1) {
                    let currentPPG = updatedPPGs[ppgIndex];

                    // Apply cascading logic for each metric that needs updating
                    [...metricKeys, ...spendKeys].forEach((metricKey) => {
                        currentPPG = computeCascadingKPIs(
                            promoConfigDictionary,
                            metricKey,
                            item[metricKey],
                            currentPPG,
                        );
                    });

                    updatedPPGs[ppgIndex] = {
                        ...currentPPG,
                        metrics: currentPPG.metrics.map((metric) => {
                            const { name_key: nameKey, allowedDecimals } =
                                promoConfigDictionary.promoMetrics[Number(metric.id)];

                            if (metricKeys.includes(nameKey) && item[nameKey] !== undefined) {
                                const formattedValue = item[nameKey].toFixed(allowedDecimals);
                                return {
                                    ...metric,
                                    value: formattedValue,
                                };
                            }
                            return metric;
                        }),
                        spendDetails: currentPPG.spendDetails.map((metric) => {
                            const { name_key: nameKey, allowedDecimals } =
                                promoConfigDictionary.spendMetrics[Number(metric.id)];

                            if (spendKeys.includes(nameKey) && item[nameKey] !== undefined) {
                                const formattedValue = item[nameKey].toFixed(allowedDecimals);
                                return {
                                    ...metric,
                                    value: formattedValue,
                                };
                            }
                            return metric;
                        }),
                    };
                }
            });
            updatePromoDetailsProperty([{ ppgs: updatedPPGs as ProductPPG[] }]);
            return res;
        },
    });

    const isStatusDropdownIsDisabled = (keyName: string): boolean => {
        if (keyName === PromoInfoFormFieldNames.status) {
            const { approval_status } = finalCalenderStatues;
            const tactic = selectedEventInfo?.currentPromoTactic;
            if (!tactic?.ppgs?.length) return false;

            const allowedStatuses = approval_status === 'approved' ? statusesForApproved : statusesForUnapproved;
            const status = tactic.status?.toLowerCase() ?? '';

            const isEditableStatus = allowedStatuses.includes(status);

            return !isEditableStatus;
        }
        return false;
    };

    const offerVariables = useMemo(() => {
        const currentPromo = selectedEventInfo?.currentPromoTactic;
        if (!triggerOfferApi) {
            return {};
        } else if (currentPromo?.promoType && currentPromo?.tacticType && currentPromo?.ppgs.length) {
            const currentPromo = selectedEventInfo?.currentPromoTactic;
            const ppgs = currentPromo?.ppgs.reduce((acc, ppg) => {
                if (ppg.isSelected) {
                    // Create a map of existing metrics for quick lookup
                    const existingMetrics = [...ppg.metrics, ...ppg.spendDetails].reduce(
                        (metricMap: Record<string, number>, metric) => {
                            const spendMetricKey = promoConfigDictionary.spendMetrics[Number(metric.id)]?.name_key;
                            const promoMetricKey = promoConfigDictionary.promoMetrics[Number(metric.id)]?.name_key;

                            if (spendMetricKey) {
                                metricMap[spendMetricKey] = Number(metric.value);
                            }
                            if (promoMetricKey) {
                                metricMap[promoMetricKey] = Number(metric.value);
                            }
                            return metricMap;
                        },
                        {},
                    );
                    const metrics: Record<string, number> = {};
                    spendMetricsForOfferLibrary.forEach((metricKey) => {
                        metrics[metricKey] = existingMetrics[metricKey] || 0;
                    });
                    promoMetricsForOfferLibrary.forEach((metricKey) => {
                        metrics[metricKey] = existingMetrics[metricKey] || 0;
                    });
                    acc.push({
                        ppg_id: ppg.id,
                        metrics,
                    });
                }
                return acc;
            }, [] as { ppg_id: number; metrics: Record<string, number> }[]);
            return {
                promotion_type: promoConfigDictionary.promotions[Number(currentPromo?.promoType)],
                tactic_description: promoConfigDictionary.tactics[Number(currentPromo?.tacticType)],
                dim2_attribute_name: selectedPlanInfo?.dim2Filter.value[0],
                sort_by: currentPromo?.suggestedOfferType,
                search_keyword: '',
                ppgs,
            };
        }
    }, [
        triggerOfferApi,
        selectedEventInfo?.currentPromoTactic?.suggestedOfferType,
        selectedEventInfo?.currentPromoTactic?.tacticType,
    ]);

    //    trigger offer api, when offer method is changing
    const { loading: offerLoading, data: offerList } = useFetchOfferData({
        method: 'post',
        queryKey: ['tpo-fetch-offer-data', offerVariables],
        url: tradePromoOptimizationUrls.promoLibraryMechanics,
        variables: offerVariables,
        queryConfig: {
            enabled: !_.isEmpty(offerVariables) && triggerOfferApi,
        },
    });

    useEffect(() => {
        if (selectedEventInfo?.currentPromoTactic)
            updateSelectedEventProperty('currentPromoTactic', {
                ...selectedEventInfo?.currentPromoTactic,
                isDataLoading: metricsRecommValuesLoading || promoLibraryPpgMetricsLoading,
            });
    }, [metricsRecommValuesLoading, promoLibraryPpgMetricsLoading]);

    const promoTypes = useMemo(() => {
        return promoConfig?.map((pr: PromoType) => ({
            value: pr.promotion_id,
            label: pr.promotion_name,
            disabled: [PromotionTypes.Write_Off, PromotionTypes.Post_Audit].includes(pr.promotion_id),
        }));
    }, [promoConfig]);

    const tacticTypes = useMemo(() => {
        if (!selectedEventInfo?.currentPromoTactic?.promoType) return [];

        return promoConfig
            ?.find((pr: PromoType) => pr.promotion_id === selectedEventInfo?.currentPromoTactic?.promoType)
            ?.tactics.map((tac: TacticType) => ({ value: tac.tactic_id, label: tac.tactic_name }));
    }, [promoConfig, selectedEventInfo?.currentPromoTactic?.promoType]);

    const fetchUpliftData = async (ppgs?: ProductPPG[]) => {
        const edlp_metric = getPromoMetricByNameKeyName(promoConfigDictionary, tpoPromoMetricKeys.EDLP_SRP);
        const upliftPPgs = ppgs?.filter((ppg) =>
            ppg.metrics.some((metric) => metric.name === edlp_metric.name && metric.value),
        );
        if (upliftPPgs && upliftPPgs.length > 0) {
            await upliftModelMutation?.mutateAsync({ mechanicType, ppgs: upliftPPgs });
        }
    };

    const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
        const blockedKeys = [
            '!',
            '@',
            '#',
            '^',
            '&',
            '*',
            '+',
            '{',
            '}',
            '[',
            ']',
            ':',
            ';',
            "'",
            '<',
            '>',
            ',',
            '?',
            '|',
            '\\',
            '~',
            '`',
        ];
        if (hasInvalidChars(e.key, blockedKeys)) {
            sendNotification('error', `Invalid key pressed: "${e.key}"`);
            e.preventDefault();
        }
    };

    const updatePromoDetailsProperty = (values: Partial<PromoTactic>[], from?: string) => {
        if (selectedEventInfo) {
            const updatedPromoTactic = values.reduce(
                (acc: PromoTactic, item: Partial<PromoTactic>) => ({
                    ...acc,
                    ...item,
                }),
                { ...selectedEventInfo.currentPromoTactic, hasChanges: true },
            );
            updateSelectedEventProperty('currentPromoTactic', updatedPromoTactic);
        }
    };

    const renderLabelWithRequiredCondition = (field: FormField) => {
        if (!field) return null;

        const labelContent = (
            <>
                {field.isRequired && <span className="text-redTriangle mr-1">*</span>}
                {field.label}:
            </>
        );

        // Conditionally wrap the label with Tooltip if tooltip exists and is not empty
        return field.tooltip ? (
            <Tooltip title={field.tooltip}>
                <span>{labelContent}</span>
            </Tooltip>
        ) : (
            <span>{labelContent}</span>
        );
    };

    const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>, label: string, maxCharLimit?: number) => {
        if (maxCharLimit && e.target.value.length > maxCharLimit) {
            sendNotification('error', `${label} Character limit exceeded. Max ${maxCharLimit} characters allowed.`);
        } else {
            updatePromoDetailsProperty([{ [e.target.name]: e.target.value }]);
        }
    };

    const onChangeTacticType = (val: any, item: any) => {
        updatePromoDetailsProperty([
            { tacticType: item?.value },
            { featureQuality: null },
            { displayQuality: null },
            { suggestedOffer: null },
            { suggestedOfferType: null },
        ]);
        setFetchMetricsTriggeredFrom(MetricsApiTriggerPoints.Tactic);
    };

    const onChangePromoType = (val: any, item: any) => {
        updatePromoDetailsProperty([
            { promoType: item?.value },
            { tacticType: null },
            { featureQuality: null },
            { displayQuality: null },
            { offer: null },
            { offerType: null },
            { manualOffer: null },
            { buyGet: null },
            { buyFor: null },
            { buySave: null },
            { discount: null },
            { parNumber: null },
            { ismCampaignId: null },
            { suggestedOffer: null },
            { suggestedOfferType: null },
        ]);
    };

    const onChangePromoStatusType = (val: any, item: any) => {
        updatePromoDetailsProperty([{ status: item?.value }]);
    };

    const onChangeFundingReq = (val: any, item: any) => {
        updatePromoDetailsProperty([{ fundingRequest: item?.value }]);
    };

    const onChangeOfferType = (val: any, item: any) => {
        let updateObj: any = {
            offerType: item?.value,
            manualOffer: null,
            offer: null,
            buyGet: null,
            buyFor: null,
            buySave: null,
            discount: null,
            suggestedOffer: null,
            suggestedOfferType: null,
        };
        if (item?.value === 'promo_library') {
            sendNotification(
                'info',
                `You can't add any further material groups when using Promo Library offer method.`,
            );
            setTempOfferDetails({
                manualOffer: selectedEventInfo?.currentPromoTactic?.manualOffer || null,
                buyGet: [
                    selectedEventInfo?.currentPromoTactic?.buyGet?.[0] || 0,
                    selectedEventInfo?.currentPromoTactic?.buyGet?.[1] || 0,
                ],
                buyFor: [
                    selectedEventInfo?.currentPromoTactic?.buyFor?.[0] || 0,
                    selectedEventInfo?.currentPromoTactic?.buyFor?.[1] || 0,
                ],
                buySave: [
                    selectedEventInfo?.currentPromoTactic?.buySave?.[0] || 0,
                    selectedEventInfo?.currentPromoTactic?.buySave?.[1] || 0,
                ],
                discount: selectedEventInfo?.currentPromoTactic?.discount || null,
                comboUnit:
                    (selectedEventInfo?.currentPromoTactic?.buyGet &&
                        selectedEventInfo?.currentPromoTactic?.comboUnit) ||
                    null,
                discountUnit: selectedEventInfo?.currentPromoTactic?.discountUnit || null,
                buySaveUnit: selectedEventInfo?.currentPromoTactic?.buySaveUnit || null,
            });
        }
        if (tempOfferDetails && item?.value === 'manual') {
            updateObj = {
                ...updateObj,
                ...tempOfferDetails,
            };
            setTempOfferDetails(null);
        }
        updatePromoDetailsProperty([updateObj]);
    };

    const onChangeManualOffer = (val: any, item: any) => {
        updatePromoDetailsProperty([
            { manualOffer: item?.value },
            { offer: null },
            { buyGet: null },
            { buyFor: null },
            { buySave: null },
            { discount: null },
        ]);
    };

    const onChangeSuggestedOfferType = (val: any, item: any) => {
        updatePromoDetailsProperty([{ suggestedOfferType: item?.value }, { suggestedOffer: null }]);
    };

    const onChangeDate = (date: number | null, key: string, copyValueTo?: string) => {
        const oldDate = selectedEventInfo?.currentPromoTactic[key];
        const newDate = date;

        if (isPromoYearChangeNotAllowed(oldDate, newDate, selectedEventInfo?.currentPromoTactic.status || '')) {
            sendNotification('error', 'Year cannot be changed once the promo is approved.');
            return false;
        }

        let updatedArray: any[] = [{ [key]: date }];

        // If promoStartDate is changed and its greater than promo end date, then clear promo end date
        if (
            key === PromoInfoFormFieldNames.PromoStartDate &&
            Number(date) > Number(selectedEventInfo?.currentPromoTactic?.promoEndDate) &&
            Number(selectedEventInfo?.currentPromoTactic?.promoEndDate) !== 0
        ) {
            updatedArray.push({ [PromoInfoFormFieldNames.PromoEndDate]: null });
        }

        // If promoStartDate is changed and its greater than cra end date, then clear cra end date
        if (
            key === PromoInfoFormFieldNames.PromoStartDate &&
            Number(date) > Number(selectedEventInfo?.currentPromoTactic?.craEndDate) &&
            Number(selectedEventInfo?.currentPromoTactic?.craEndDate) !== 0
        ) {
            updatedArray.push({ [PromoInfoFormFieldNames.CRAEndDate]: null });
        }

        // Handle CRA dates copy logic
        if (copyValueTo && date) {
            const planYear = selectedPlanInfo?.planYear;
            if (planYear) {
                const yearStart = dayjs().utc().set('year', planYear).startOf('year').unix();
                const yearEnd = dayjs().utc().set('year', planYear).endOf('year').unix();

                if (
                    date >= yearStart &&
                    date <= yearEnd &&
                    (key === PromoInfoFormFieldNames.PromoEndDate
                        ? date >= selectedEventInfo?.currentPromoTactic[PromoInfoFormFieldNames.CRAStartDate]
                        : true) //if we are changing promo end date and it is greater than cra start date, then change cra end date as well
                ) {
                    updatedArray.push({ [copyValueTo]: date });
                } else {
                    tpoLogger.debug('Date not copied - out of plan year', {
                        copyValueTo,
                        date,
                        planYear,
                        dateInRange: { start: yearStart, end: yearEnd },
                    });
                }
            }
        }
        updatedArray = [...updatedArray, ...[{ suggestedOfferType: null }, { suggestedOffer: null }]];
        updatePromoDetailsProperty(updatedArray);

        if (date) {
            setFetchMetricsTriggeredFrom(MetricsApiTriggerPoints.Date);
        }
    };

    const normalizeStatus = (input: string): string => {
        if (!input) return '';
        return String(input)
            .trim()
            .replace(/[_\s\-]+/g, '_')
            .replace(/[^A-Za-z0-9_]+/g, '_')
            .replace(/^_+|_+$/g, '')
            .toLowerCase();
    };

    const getPromoStatusDropdownOptions = (
        currentStatus: string,
    ): { label: string; value: string; disabled: boolean }[] => {
        if (!currentStatus) return [];
        const { approval_status } = finalCalenderStatues;
        const status = normalizeStatus(currentStatus) || '';

        const allowedStatuses =
            approval_status === 'approved'
                ? allowedTransitionsApproved[status]
                : allowedTransitionsUnapproved[status] || [];

        return promoStatusDropdownOptions.map((option) => ({
            ...option,
            disabled: !allowedStatuses?.includes(option.value) && option.value !== status,
        }));
    };

    const getDropDownOptionsAndCustomOnChange = (fieldName: string) => {
        switch (fieldName) {
            case PromoInfoFormFieldNames.PromoType:
                return { options: promoTypes, onChange: onChangePromoType };
            case PromoInfoFormFieldNames.TacticType:
                return { options: tacticTypes, onChange: onChangeTacticType };
            case PromoInfoFormFieldNames.OfferMethod:
                return {
                    options: offerMethodDropdownOptions.map((option) => ({
                        ...option,
                        disabled: option.value === 'promo_library' && promoActiveTab?.toString() === '0',
                    })),
                    onChange: onChangeOfferType,
                };
            case PromoInfoFormFieldNames.ManualOffer:
                return {
                    options: manualOfferDropdownOptions,
                    onChange: onChangeManualOffer,
                };
            case PromoInfoFormFieldNames.DisplayQuality:
                return { options: displayQualityDropdownOptions };
            case PromoInfoFormFieldNames.FeatureQuality:
                return { options: featureQualityDropdownOptions };
            case PromoInfoFormFieldNames.status:
                return {
                    options: getPromoStatusDropdownOptions(selectedEventInfo?.currentPromoTactic?.status || ''),
                    onChange: onChangePromoStatusType,
                };
            case PromoInfoFormFieldNames.FundingRequest:
                return { options: promoFundingReqDropdownOptions, onChange: onChangeFundingReq };
            case PromoInfoFormFieldNames.SuggestedOfferType:
                return { options: suggestedOfferTypeDropdownOptions, onChange: onChangeSuggestedOfferType };
            default:
                return { options: [] };
        }
    };

    const getCustomOnChangeDate = (fieldName: string) => {
        switch (fieldName) {
            case PromoInfoFormFieldNames.PromoStartDate:
            case PromoInfoFormFieldNames.PromoEndDate:
                return {
                    onChange: onChangeDate,
                };
            case PromoInfoFormFieldNames.CRAStartDate: {
                return {
                    onChange: (date: number | null, key: string) => {
                        const updationArr = [{ [key]: date }];
                        //if cra start date is greater than cra end date, then clear cra end date
                        if (
                            Number(date) > Number(selectedEventInfo?.currentPromoTactic?.craEndDate) &&
                            Number(selectedEventInfo?.currentPromoTactic?.craEndDate) !== 0
                        ) {
                            updationArr.push({
                                [PromoInfoFormFieldNames.CRAEndDate]: null,
                            });
                        }
                        // //if cra start date is greater than promo end date, then clear promo end date
                        // if (
                        //     Number(date) > Number(selectedEventInfo?.currentPromoTactic?.promoEndDate) &&
                        //     Number(selectedEventInfo?.currentPromoTactic?.promoEndDate) !== 0
                        // ) {
                        //     updationArr.push({
                        //         [PromoInfoFormFieldNames.PromoEndDate]: null,
                        //     });
                        // }
                        if (
                            Number(date) > Number(selectedEventInfo?.currentPromoTactic?.promoStartDate) &&
                            Number(selectedEventInfo?.currentPromoTactic?.promoStartDate) !== 0
                        ) {
                            sendNotification('info', 'CRA Start date is greater than Promo Start Date');
                        }
                        setFetchMetricsTriggeredFrom(MetricsApiTriggerPoints.Date);
                        updatePromoDetailsProperty(updationArr);
                    },
                };
            }
            default:
                return { onChange: null };
        }
    };

    const isPostAuditOrWriteOff = [PromotionTypes.Post_Audit, PromotionTypes.Write_Off].includes(
        Number(selectedEventInfo?.currentPromoTactic?.promoType),
    );

    const combinedViewMode =
        isOptimizationInProgress || viewMode || isTpmTab || isCurrentPromoFormDisabled || isPostAuditOrWriteOff;

    const readConditions = (conditions: ConditionRule[], conjunction: 'all' | 'any') => {
        switch (conjunction) {
            case 'all': {
                return conditions.every((item) => {
                    const { field, operator, value } = item;
                    return compareValues(value, selectedEventInfo?.currentPromoTactic?.[field], operator);
                });
            }
            case 'any': {
                return conditions.some((item) => {
                    const { operator, value, field } = item;
                    return compareValues(value, selectedEventInfo?.currentPromoTactic?.[field], operator);
                });
            }
            default:
                return false;
        }
    };

    const compareValues = (targetValue: any, value: any, operator: OperatorTypes) => {
        switch (operator) {
            case OperatorTypes.isEqual:
                return value === targetValue;
            case OperatorTypes.isNotEqual:
                return value !== targetValue;

            case OperatorTypes.isEmpty:
                return !value;
            case OperatorTypes.isNotEmpty:
                return !!value;
            case OperatorTypes.In:
                return targetValue.includes(value);
            case OperatorTypes.NotIn:
                return !targetValue.includes(value);
            default:
                return false;
        }
    };

    const getFieldProperties = (conditions: FieldCondition | undefined) => {
        if (!conditions) return false;
        const { rules, action } = conditions;
        const shouldHide = rules.some((rule) => {
            const { conjunction, conditions } = rule;
            if (readConditions(conditions, conjunction)) {
                return true;
            }
        });

        return {
            [action]: shouldHide,
        };
    };

    const getEnabledDates = (formField: string) => {
        const planYear = selectedPlanInfo?.planYear;
        if (!planYear) return {};

        const today = dayjs().startOf('day').unix();
        switch (formField) {
            case 'promoStartDate': {
                const craStartDate = selectedEventInfo?.currentPromoTactic?.craStartDate;
                const planStart = dayjs().set('year', planYear).startOf('year').startOf('day').unix(); // Jan 1 of plan year
                //   const allowedStartDate = Math.max(Number(craStartDate ?? 0), planStart, today);
                const allowedStartDate = Math.max(planStart, today);
                return {
                    start: allowedStartDate,
                    end: Math.max(
                        dayjs()
                            .utc()
                            .set('year', planYear + 1)
                            .endOf('year')
                            .startOf('day')
                            .unix(), // Dec 31, planYear+1
                        today,
                    ),
                };
            }

            case 'promoEndDate': {
                const promoStart = selectedEventInfo?.currentPromoTactic?.promoStartDate ?? 0;
                const formattedPromoStart = dayjs.unix(promoStart).utc().format('MM/DD/YYYY');
                const formattedPromoStartUnix = dayjs(formattedPromoStart, 'MM/DD/YYYY').startOf('day').unix();

                const fallbackStart = dayjs().set('year', planYear).startOf('year').startOf('day').unix();
                const allowedStart = Math.max(formattedPromoStartUnix, fallbackStart, today);
                return {
                    start: allowedStart,
                    end: Math.max(
                        dayjs()
                            .utc()

                            .set('year', planYear + 1)
                            .endOf('year')
                            .startOf('day')
                            .unix(), // Dec 31, planYear+1
                        today,
                    ),
                };
            }

            case 'craStartDate': {
                const start = Math.max(dayjs().set('year', planYear).startOf('year').startOf('day').unix(), today);

                return {
                    start: start,
                    end: Math.max(
                        dayjs().utc().set('year', planYear).endOf('year').startOf('day').unix(), // Dec 31, planYear
                        today,
                    ),
                };
            }

            case 'craEndDate': {
                const craStart = selectedEventInfo?.currentPromoTactic?.craStartDate ?? 0;
                const formattedPromoStart = dayjs.unix(craStart).utc().format('MM/DD/YYYY');
                const formattedPromoStartUnix = dayjs(formattedPromoStart, 'MM/DD/YYYY').startOf('day').unix();
                const fallbackStart = dayjs().set('year', planYear).startOf('year').startOf('day').unix();
                const allowedStart = Math.max(formattedPromoStartUnix, fallbackStart, today);

                return {
                    start: allowedStart,
                    end: Math.max(
                        dayjs().utc().set('year', planYear).endOf('year').startOf('day').unix(), // Dec 31, planYear
                        today,
                    ),
                };
            }

            default:
                return {};
        }
    };

    const renderFormField = (field: FormField, isFieldDisabled: boolean) => {
        const { name, placeholder, copyValueTo, maxCharLimit, label, units, autoPopulate } = field;

        const renderFieldContent = () => {
            switch (field.type) {
                case FieldTypes.Text:
                    return (
                        <Input
                            className="!w-full !rounded-md"
                            value={selectedEventInfo?.currentPromoTactic?.[name]}
                            name={name}
                            onChange={(e) => handleInputChange(e, label as string, maxCharLimit)}
                            disabled={combinedViewMode || isFieldDisabled}
                            onKeyDown={handleKeyDown}
                        />
                    );
                case FieldTypes.Select: {
                    const { options, onChange } = getDropDownOptionsAndCustomOnChange(name);
                    return (
                        <CustomSelect
                            suffixIcon={<DownSvg dropDownStroke={colorConstants.greyAlt17} />}
                            className="dropdown-height !w-full overflow-hidden"
                            value={selectedEventInfo?.currentPromoTactic?.[name]}
                            options={options}
                            onChange={
                                onChange ??
                                ((val, option: any) => updatePromoDetailsProperty([{ [name]: option?.value }]))
                            }
                            allowClear={allowDropdownClearProperties.includes(name)}
                            disabled={combinedViewMode || isFieldDisabled || isStatusDropdownIsDisabled(name)}
                        />
                    );
                }
                case FieldTypes.Date: {
                    const { onChange } = getCustomOnChangeDate(name);
                    const enabledDates = getEnabledDates(name);

                    return (
                        <DatePickerWithEpoch
                            value={selectedEventInfo?.currentPromoTactic?.[name] || 0} // Pass the epoch timestamp
                            onChange={(date: number | null) => {
                                if (onChange) {
                                    onChange(date, name, copyValueTo);
                                } else {
                                    if (name === PromoInfoFormFieldNames.CRAEndDate) {
                                        setFetchMetricsTriggeredFrom(MetricsApiTriggerPoints.Date);
                                    }
                                    const updationArr = [{ [name]: date }];
                                    if (copyValueTo) {
                                        updationArr.push({ [copyValueTo]: date });
                                    }
                                    updatePromoDetailsProperty(updationArr);
                                }
                            }}
                            format="MM/DD/YYYY" // Specify date format
                            placeholder={placeholder}
                            showToday={true}
                            classNames="!w-full !rounded-md"
                            enabledDates={enabledDates}
                            disabled={combinedViewMode || isFieldDisabled}
                            defaultOpenDate={
                                (selectedPlanInfo?.planYear || 0) > dayjs().year()
                                    ? dayjs()
                                          .set('year', selectedPlanInfo?.planYear || 0)
                                          .startOf('year')
                                          .unix()
                                    : null
                            }
                        />
                    );
                }
                case FieldTypes.PriceOff:
                    return (
                        <div className="relative w-full">
                            <Input
                                type="text"
                                onKeyDown={(e: React.KeyboardEvent<HTMLInputElement>) => {
                                    const currentValue = selectedEventInfo?.currentPromoTactic?.[name] ?? '';
                                    handleNumberInputKeyDown(e, currentValue);
                                }}
                                onChange={(e: any) => {
                                    const rawValue = e.target.value;
                                    // Update local state immediately with raw string value (preserves trailing zeros)
                                    updatePromoDetailsProperty([{ [name]: rawValue }]);
                                }}
                                disabled={combinedViewMode || isFieldDisabled}
                                placeholder={placeholder}
                                className="!w-full !rounded-md"
                                value={selectedEventInfo?.currentPromoTactic?.[name] ?? ''}
                                suffix={
                                    <div className="flex space-x-2">
                                        <span
                                            onClick={(e) => {
                                                e.stopPropagation();
                                                if (combinedViewMode || isFieldDisabled) return;
                                                updatePromoDetailsProperty([{ discountUnit: 'abs' }]);
                                            }}
                                            className={`mr-2 cursor-pointer ${
                                                selectedEventInfo?.currentPromoTactic?.discountUnit === 'abs'
                                                    ? 'text-purpleFont font-bold'
                                                    : 'text-promoColour'
                                            }`}
                                        >
                                            {currency}
                                        </span>
                                        <span
                                            onClick={(e) => {
                                                e.stopPropagation();
                                                if (combinedViewMode || isFieldDisabled) return;
                                                updatePromoDetailsProperty([{ discountUnit: '%' }]);
                                            }}
                                            className={`cursor-pointer ${
                                                selectedEventInfo?.currentPromoTactic?.discountUnit === '%'
                                                    ? 'text-purpleFont font-bold'
                                                    : 'text-promoColour'
                                            }`}
                                        >
                                            %
                                        </span>
                                    </div>
                                }
                            />
                        </div>
                    );
                case FieldTypes.Range:
                    return (
                        <div className="w-full flex items-center">
                            {/* <RangeInputCustom
                                maxVal={
                                    selectedEventInfo?.currentPromoTactic?.[name]?.length
                                        ? selectedEventInfo.currentPromoTactic?.[name]?.[1]
                                        : 0
                                }
                                minVal={
                                    selectedEventInfo?.currentPromoTactic?.[name]?.length
                                        ? selectedEventInfo.currentPromoTactic?.[name]?.[0]
                                        : 0
                                }
                                onMaxChange={(val) =>
                                    updatePromoDetailsProperty([
                                        {
                                            [name]: Array.isArray(selectedEventInfo?.currentPromoTactic?.[name])
                                                ? [selectedEventInfo?.currentPromoTactic?.[name]?.[0] || 0, val]
                                                : [0, val],
                                        },
                                    ])
                                }
                                onMinChange={(val) => {
                                    updatePromoDetailsProperty([
                                        {
                                            [name]: Array.isArray(selectedEventInfo?.currentPromoTactic?.[name])
                                                ? [val, selectedEventInfo?.currentPromoTactic?.[name]?.[1] || 0]
                                                : [val, 0],
                                        },
                                    ]);
                                }}
                                inputClassName="max-w-[80px]"
                                placeHolder={[undefined, undefined]}
                                disabled={combinedViewMode || isFieldDisabled}
                            /> */}
                            {/* <CombinedInput
                                labelFirst={}
                                labelSecond="Max"
                                valueFirst={selectedEventInfo?.currentPromoTactic?.[name]?.length
                                    ? selectedEventInfo.currentPromoTactic?.[name]?.[0]
                                    : 0
                                }
                                valueSecond={selectedEventInfo?.currentPromoTactic?.[name]?.length
                                    ? selectedEventInfo.currentPromoTactic?.[name]?.[1]
                                    : 0
                                }
                            /> */}
                            {units?.unitArray?.length && (
                                <div className="flex space-x-0.5">
                                    {units.unitArray.map((unit) => (
                                        <span
                                            key={unit.value}
                                            onClick={() => {
                                                if (combinedViewMode || isFieldDisabled) return;
                                                updatePromoDetailsProperty([{ [units.unitKey]: unit.value }]);
                                            }}
                                            className={`ml-1 cursor-pointer ${
                                                selectedEventInfo?.currentPromoTactic?.[units.unitKey] === unit.value
                                                    ? 'text-purpleFont font-bold'
                                                    : 'text-promoColour'
                                            }`}
                                        >
                                            {unit.label}
                                        </span>
                                    ))}
                                </div>
                            )}
                        </div>
                    );
                case FieldTypes.OfferLibrary:
                    return (
                        <OfferDropdown
                            disabled={combinedViewMode || isFieldDisabled}
                            onDropdownVisibleChange={(open) => {
                                if (open) setTriggerOfferApi(true);
                                else setTriggerOfferApi(false);
                            }}
                            data={offerList}
                            placeholder={placeholder}
                            className="dropdown-height !w-full !rounded-md offer-dropdown"
                            loading={offerLoading}
                            onChange={(val, option: any) => updatePromoDetailsProperty([{ [name]: option?.value }])}
                            value={selectedEventInfo?.currentPromoTactic?.[name]}
                        />
                    );
                default:
                    return null;
            }
        };

        // Wrap the field content with auto-populate icon if enabled
        if (autoPopulate?.enabled) {
            return (
                <div className="relative w-full">
                    {renderFieldContent()}
                    {!(combinedViewMode || isFieldDisabled) && (
                        <div className="absolute right-[-32px] top-1/2 transform -translate-y-1/2">
                            <AutoPopulateIcon
                                onPopulate={() => handleAutoPopulate(name)}
                                tooltip={autoPopulate.tooltip}
                                disabled={combinedViewMode || isFieldDisabled}
                            />
                        </div>
                    )}
                </div>
            );
        }

        return renderFieldContent();
    };

    const handleRefresh = () => {
        const currentPromoTactic = selectedEventInfo?.currentPromoTactic;
        if (currentPromoTactic) {
            updateSelectedEventProperty('currentPromoTactic', { ...currentPromoTactic, isLoading: true });
            queryClient.refetchQueries({
                queryKey: ['tpo_get_promo_details_by_id'],
                exact: false, // useful if the query key has dynamic parts
            });
        }
    };

    const popOverItems = useMemo(() => {
        const hoverItems: PopoverItem[] = [];
        if (dimensionConfigData?.dim1Attribute?.label) {
            hoverItems.push({
                title: dimensionConfigData?.dim1Attribute?.label,
                content: selectedPlanInfo?.dim1Filter?.value?.join(', ') || '',
            });
        }
        if (dimensionConfigData?.dim2Attribute?.label) {
            hoverItems.push({
                title: dimensionConfigData?.dim2Attribute?.label,
                content: selectedPlanInfo?.dim2Filter?.value?.join(', ') || '',
            });
        }

        if (selectedPlanInfo?.division) {
            hoverItems.push({ title: 'Customer Division', content: selectedPlanInfo?.divisionName || '' });
        }

        hoverItems.push({
            title: 'TPO Promo ID',
            content: promoActiveTab?.toString() === '0' ? '-' : promoActiveTab?.toString() || '',
        });

        if (tempEventProperties?.name) {
            hoverItems.push({ title: 'Parent Event Name', content: tempEventProperties.name });
        }
        return hoverItems;
    }, [tempEventProperties, selectedPlanInfo]);

    const renderCombinedInput = (field: FormField, isFieldDisabled: boolean) => {
        const { units } = field;
        return (
            <div key={field.name} className="flex gap-2 w-full items-center">
                <div
                    className={`min-w-0 flex items-center ml-[38px] ${
                        upliftModelMutation?.isPending || metricsRecommValuesLoading || isSavePending
                            ? 'pointer-events-none'
                            : ''
                    }`}
                >
                    {field.isRequired && <span className="text-redTriangle mr-2">*</span>}
                    <CombinedInput
                        labelFirst={field.label[0]}
                        labelSecond={field.label[1]}
                        valueFirst={
                            selectedEventInfo?.currentPromoTactic?.[field.name]?.length
                                ? selectedEventInfo.currentPromoTactic?.[field.name]?.[0]
                                : 0
                        }
                        valueSecond={
                            selectedEventInfo?.currentPromoTactic?.[field.name]?.length
                                ? selectedEventInfo.currentPromoTactic?.[field.name]?.[1]
                                : 0
                        }
                        onChangeFirst={(val) =>
                            updatePromoDetailsProperty([
                                { [field.name]: [val, selectedEventInfo?.currentPromoTactic?.[field.name]?.[1] || 0] },
                            ])
                        }
                        onChangeSecond={(val) =>
                            updatePromoDetailsProperty([
                                { [field.name]: [selectedEventInfo?.currentPromoTactic?.[field.name]?.[0] || 0, val] },
                            ])
                        }
                        classNameFirst="max-w-[80px]"
                        classNameSecond="max-w-[80px]"
                        firstGroupClassName="whitespace-nowrap"
                    />
                    {units?.unitArray?.length && (
                        <div className="flex space-x-0.5 ml-2">
                            {units.unitArray.map((unit) => (
                                <span
                                    key={unit.value}
                                    onClick={() => {
                                        if (combinedViewMode || isFieldDisabled) return;
                                        updatePromoDetailsProperty([{ [units.unitKey]: unit.value }]);
                                    }}
                                    className={`ml-1 cursor-pointer ${
                                        selectedEventInfo?.currentPromoTactic?.[units.unitKey] === unit.value
                                            ? 'text-purpleFont font-bold'
                                            : 'text-promoColour'
                                    }`}
                                >
                                    {unit.label}
                                </span>
                            ))}
                        </div>
                    )}
                </div>
            </div>
        );
    };

    return (
        <div className="bg-backgroundGreyAlt6 p-3 rounded">
            <div
                className="flex justify-between items-center cursor-pointer"
                onClick={() => setIsExpanded(!isExpanded)}
            >
                <div className="flex gap-2 items-center">
                    <span className="font-bold">Add Promo Details</span>
                    <IconTextPopover icon={InfoCircleOutlined} text="Promo Details" items={popOverItems} />
                    {promoPopupErrors.length > 0 && (
                        <div className="mx-3 flex items-center gap-1" onClick={(e) => e.stopPropagation()}>
                            <InfoCircleOutlined style={{ color: colorConstants.redAlt }} />
                            <ErrorPopover promoPopupErrors={promoPopupErrors} />
                        </div>
                    )}
                    {selectedEventInfo?.currentPromoTactic?.isRefreshRequired && (
                        <button onClick={handleRefresh}> refresh</button>
                    )}
                </div>
                {isExpanded ? (
                    <UpOutlined className="!text-purpleFont" />
                ) : (
                    <DownOutlined className="!text-purpleFont" />
                )}
            </div>
            {isExpanded && (
                <>
                    <div className="flex flex-wrap gap-x-4 mt-3 items-center text-black54">
                        <div>Below LUC: {selectedEventInfo?.currentPromoTactic?.belowLUC}</div>
                        <div>Below MPIC: {selectedEventInfo?.currentPromoTactic?.belowMPIC}</div>
                        <div>
                            Event Date:
                            {tempEventProperties.CRAStartDate && tempEventProperties.CRAEndDate
                                ? ` ${tempEventProperties.CRAStartDate} - ${tempEventProperties.CRAEndDate}`
                                : ' -'}
                        </div>
                        <div>
                            Approval Date:
                            {selectedEventInfo?.currentPromoTactic?.approvedDate
                                ? ` ${dayjs(selectedEventInfo?.currentPromoTactic?.approvedDate * 1000)
                                      .startOf('day')
                                      .format(dateFormat)}`
                                : ' -'}
                        </div>
                        <div>Mechanic: {mechanicType}</div>
                    </div>
                    {/* PRODUCT INFO FORM */}
                    <div className="grid grid-rows-5 grid-flow-col grid-cols-[280px_300px_280px_300px] gap-y-4 xl:gap-x-4 2xl:gap-x-16 w-full py-3 justify-center items-center">
                        {promoInfoFormFields.map((field) => {
                            // const fieldProperties: any = getFieldProperties(field.conditions);
                            const { isDisabled, isHidden } = field;
                            if (isHidden) return null;
                            return field.type === FieldTypes.Range ? (
                                renderCombinedInput(field, !!isDisabled)
                            ) : (
                                <div key={field.name} className="flex gap-4 w-full items-center">
                                    <div className="basis-5/12 text-right flex gap-1 justify-end items-center">
                                        {renderLabelWithRequiredCondition(field)}
                                    </div>
                                    <div
                                        className={`basis-7/12 min-w-0 flex items-center ${
                                            upliftModelMutation?.isPending ||
                                            metricsRecommValuesLoading ||
                                            isSavePending
                                                ? 'pointer-events-none'
                                                : ''
                                        }`}
                                    >
                                        {renderFormField(field, !!isDisabled)}
                                    </div>
                                </div>
                            );
                        })}
                    </div>
                </>
            )}
        </div>
    );
};

export default PromoInfoSection;
